import { InformationalBanner, MoneyBankNotesIcon } from '@wonderschool/common-base-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { hasFirstStripeInvoicePaid } from '../../helpers/stripe';
import { useOrganization } from '../../hooks/useOrganizations';

import PayoutVerificationModal from './PayoutVerificationModal';

/**
 * Shown to providers not yet verified by our support team (`stripe.paymentsEnabled`
 * is false). Providers must be verified before they can connect a payout account, so
 * we prompt them to get verified here instead of showing the connect option.
 *
 * Where it appears:
 *  - Finances settings: always, so a provider can request verification before sending
 *    a single invoice. Invoices are not a prerequisite for Stripe onboarding.
 *  - Billing pages (SetupPayoutBanner): only once the first invoice is paid via Stripe,
 *    as a just-in-time nudge.
 *
 * States:
 *  - No pending request, first Stripe payment received: a positive `success` banner
 *    ("you got paid") whose CTA opens the self-serve intake form
 *    (PayoutVerificationModal, CCMS-7942).
 *  - No pending request, no payment yet: a neutral `info` banner with the same CTA.
 *  - Request pending (`payoutVerification.status === 'pending'`): an `info` banner
 *    telling them the request is in review, with no CTA.
 */
export default function VerifyPayoutBanner() {
  const { t } = useTranslation();
  const organization = useOrganization();
  const [open, setOpen] = useState(false);

  const isInReview = organization?.payoutVerification?.status === 'pending';
  const hasBeenPaid = hasFirstStripeInvoicePaid(organization);

  if (isInReview) {
    return (
      <div className="my-4">
        <InformationalBanner
          type="info"
          icon={<MoneyBankNotesIcon className="h-5 w-5" />}
          label={t('verifyPayout.inReviewMessage')}
          data-testid="verify-payout-banner"
        />
      </div>
    );
  }

  return (
    <div className="my-4">
      <InformationalBanner
        type={hasBeenPaid ? 'success' : 'info'}
        icon={<MoneyBankNotesIcon className="h-5 w-5" />}
        label={t(hasBeenPaid ? 'verifyPayout.message' : 'verifyPayout.setupMessage')}
        linkLabel={t('verifyPayout.verifyCta')}
        onClickLink={() => setOpen(true)}
        data-testid="verify-payout-banner"
      />
      <PayoutVerificationModal organization={organization} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
